/**
 * DynamicEntryList Component
 *
 * Reusable dynamic list for adding/removing/editing rows of structured entries.
 * Each row contains fields defined by the `fields` prop, plus a remove button.
 *
 * Used by PlannedExpensesList, InvestmentPropertiesList, VehiclesList, etc.
 */

import React from "react";
import { Trash2, Plus } from "lucide-react";
import { cn } from "../utils/cn";

/**
 * Describes a single field within each dynamic entry row.
 */
export interface DynamicEntryField {
  /** Property name on the entry object */
  name: string;
  /** Input type to render */
  type: "text" | "currency" | "percentage" | "year" | "select";
  /** Placeholder text for the input */
  placeholder: string;
  /** Options for 'select' type fields */
  options?: { value: string; label: string }[];
}

export interface DynamicEntryListProps {
  /** Array of entry objects, each containing field values */
  entries: Record<string, unknown>[];
  /** Field definitions for each row */
  fields: DynamicEntryField[];
  /** Called when user clicks "Add" */
  onAdd: () => void;
  /** Called with entry index when user clicks "Remove" */
  onRemove: (index: number) => void;
  /** Called with (index, fieldName, value) when a field changes */
  onChange: (index: number, field: string, value: unknown) => void;
  /** Label for the add button (e.g., "Add planned expense") */
  addLabel: string;
  /** Label shown when there are no entries */
  emptyLabel: string;
  /** Maximum number of entries (default: 10) */
  maxEntries?: number;
  /** Optional component id */
  id?: string;
  /** Optional additional class names */
  className?: string;
}

/**
 * Renders a single field input based on its type.
 */
function renderField(
  field: DynamicEntryField,
  value: unknown,
  onChange: (value: unknown) => void,
  rowIndex: number
): React.ReactNode {
  const key = `${rowIndex}-${field.name}`;

  switch (field.type) {
    case "text":
      return (
        <input
          key={key}
          type="text"
          className="dynamic-entry-input dynamic-entry-text"
          placeholder={field.placeholder}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "currency":
      return (
        <div key={key} className="dynamic-entry-currency-wrapper">
          <span className="dynamic-entry-currency-prefix">$</span>
          <input
            type="number"
            className="dynamic-entry-input dynamic-entry-currency"
            placeholder={field.placeholder}
            value={
              value === null || value === undefined ? "" : (value as number)
            }
            onChange={(e) => {
              const num = e.target.value === "" ? null : Number(e.target.value);
              onChange(num);
            }}
            min="0"
            step="1000"
            onWheel={(e) => e.currentTarget.blur()}
          />
        </div>
      );

    case "year":
      return (
        <input
          key={key}
          type="number"
          className="dynamic-entry-input dynamic-entry-year"
          placeholder={field.placeholder}
          value={value === null || value === undefined ? "" : (value as number)}
          onChange={(e) => {
            const num = e.target.value === "" ? null : Number(e.target.value);
            onChange(num);
          }}
          min="2000"
          max="2100"
          step="1"
          onWheel={(e) => e.currentTarget.blur()}
        />
      );

    case "percentage":
      return (
        <input
          key={key}
          type="number"
          className="dynamic-entry-input dynamic-entry-percentage"
          placeholder={field.placeholder}
          value={value === null || value === undefined ? "" : (value as number)}
          onChange={(e) => {
            const num = e.target.value === "" ? null : Number(e.target.value);
            onChange(num);
          }}
          min="0"
          max="100"
          step="0.1"
          onWheel={(e) => e.currentTarget.blur()}
        />
      );

    case "select":
      return (
        <select
          key={key}
          className="dynamic-entry-input dynamic-entry-select"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{field.placeholder}</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
  }
}

/**
 * DynamicEntryList — reusable add/remove/edit row list
 *
 * Renders a list of structured entry rows with configurable fields.
 * Supports text, currency, percentage, year, and select field types.
 */
export const DynamicEntryList: React.FC<DynamicEntryListProps> = ({
  entries,
  fields,
  onAdd,
  onRemove,
  onChange,
  addLabel,
  emptyLabel,
  maxEntries = 10,
  id,
  className,
}) => {
  const isMaxReached = entries.length >= maxEntries;

  return (
    <div id={id} className={cn("dynamic-entry-list", className)}>
      {entries.length === 0 ? (
        <div className="dynamic-entry-empty">
          <span>{emptyLabel}</span>
          <button
            type="button"
            className="dynamic-entry-add-button"
            onClick={onAdd}
            disabled={isMaxReached}
          >
            <Plus size={16} /> {addLabel}
          </button>
        </div>
      ) : (
        <div role="list" className="dynamic-entry-rows">
          {entries.map((entry, index) => (
            <div key={index} role="listitem" className="dynamic-entry-row">
              {fields.map((field) =>
                renderField(
                  field,
                  entry[field.name],
                  (value) => onChange(index, field.name, value),
                  index
                )
              )}
              <button
                type="button"
                className="dynamic-entry-remove-button"
                onClick={() => onRemove(index)}
                aria-label={`Remove entry ${index + 1}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
      {entries.length > 0 && (
        <button
          type="button"
          className="dynamic-entry-add-button"
          onClick={onAdd}
          disabled={isMaxReached}
        >
          <Plus size={16} /> {addLabel}
        </button>
      )}
    </div>
  );
};

export default DynamicEntryList;
